/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { ButtonBase } from "../ButtonBase";
import { isFunction, isUndefined } from "@adapptio/lib-shared";

import "./SwitchBase.scss";

export interface ISwitchBaseProps {
	className?: string;
	value?: string;
	checked?: boolean;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>, newChecked: boolean) => void;
	type?: React.ComponentPropsWithoutRef<"input">["type"];
	icon?: React.ReactNode;
	checkedIcon?: React.ReactNode;
}

const SwitchBase: React.FC<ISwitchBaseProps> = ({
	className,
	type,
	value,
	checked,
	onChange,
	icon,
	checkedIcon
}) => {
	const baseClass = BEM("edt-switch-base");

	if (className) {
		baseClass.add(className);
	}

	const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			if (event.nativeEvent.defaultPrevented) {
				return;
			}

			const newChecked = event.target.checked;

			if (isFunction(onChange)) {
				onChange(event, newChecked);
			}
		},
		[ onChange ]
	);

	return (
		<ButtonBase Component="span" className={baseClass.toClassName()}>
			<input
				className={baseClass.element("input").toClassName()}
				type={type}
				onChange={handleChange}
				checked={checked}
				{...(type === "checkbox" && isUndefined(value) ? {} : { value })}
			/>
			{!isUndefined(checkedIcon) && checked ? checkedIcon : icon}
		</ButtonBase>
	);
};

SwitchBase.defaultProps = {
	type: "checkbox"
};

const SwitchBaseMemo = React.memo(SwitchBase);

export { SwitchBaseMemo as SwitchBase };
